/* __next_internal_client_entry_do_not_use__ Default auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo, startTransition, useRef } from 'react';
import { tv } from 'tailwind-variants';
import Cookies from 'js-cookie';
import { useSitecore } from '@sitecore-content-sdk/nextjs';
import { SvgIcon } from 'helpers/SvgIcon';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { getTestProps } from 'lib/testing/utils';
import RemixIcon from 'helpers/RemixIcon/RemixIcon';
const DEFAULT_ALERT_TYPE = 'neutral';
const COOKIE_PREFIX = 'alert-dismissed-';
// Tracks rendering per route/type to prevent duplicate instances on a page
const renderedInstanceKeys = new Set();
// Parses a date string (including UTC with 'Z') into a JavaScript Date object in local time
const parseLocal = (dateString)=>{
    if (!dateString) return null;
    // Handle Sitecore's default empty date
    if (dateString === '0001-01-01T00:00:00Z') return null;
    return new Date(dateString);
};
// Checks if an alert is currently active based on its start and end dates
const isAlertActive = (alert)=>{
    const { startDate, endDate } = alert || {};
    const now = new Date();
    const start = parseLocal(startDate?.jsonValue?.value);
    const end = parseLocal(endDate?.jsonValue?.value);
    if (!start && !end) return true;
    if (start && now < start) return false;
    if (end && now > end) return false;
    return true;
};
// Custom hook to manage alert state and logic
const useAlerts = (alertTypeGuid)=>{
    const { page } = useSitecore();
    const siteSettings = page.layout.sitecore.context?.siteSettings;
    const siteAlerts = useMemo(()=>siteSettings?.siteAlerts ?? [], [
        siteSettings?.siteAlerts
    ]);
    const [state, setState] = useState({
        dismissing: null,
        hydrated: false,
        dismissedAlerts: new Set(),
        alreadyRendered: false,
        renderKey: null
    });
    const claimedRef = useRef(false);
    useEffect(()=>{
        const dismissedFromCookies = new Set();
        siteAlerts.forEach((alert)=>{
            if (alert?.id && Cookies.get(COOKIE_PREFIX + alert.id)) {
                dismissedFromCookies.add(alert.id);
            }
        });
        // Compute a per-route key to ensure only one Alert instance renders
        const pathname = "object" !== 'undefined' ? window.location.pathname : '';
        const typeKey = (alertTypeGuid || 'all').toLowerCase();
        const renderKey = `${typeKey}::${pathname}`;
        // Claim the slot if not already claimed. Handle StrictMode by releasing on cleanup.
        let wasAlreadyRendered = false;
        if ("object" !== 'undefined') {
            if (!renderedInstanceKeys.has(renderKey)) {
                renderedInstanceKeys.add(renderKey);
                claimedRef.current = true;
            } else {
                wasAlreadyRendered = true;
            }
        }
        startTransition(()=>{
            setState((prev)=>({
                    ...prev,
                    dismissedAlerts: dismissedFromCookies,
                    hydrated: true,
                    alreadyRendered: wasAlreadyRendered,
                    renderKey
                }));
        });
        return ()=>{
            if (claimedRef.current) {
                renderedInstanceKeys.delete(renderKey);
                claimedRef.current = false;
            }
        };
    }, [
        siteAlerts,
        alertTypeGuid
    ]);
    const matchedAlerts = useMemo(()=>siteAlerts.filter((alert)=>{
            const alertTypeGuidFromAlert = alert?.alertType?.jsonValue?.id;
            const expectedGuid = alertTypeGuid?.toLowerCase();
            const matchesType = !expectedGuid || alertTypeGuidFromAlert?.toLowerCase() === expectedGuid;
            const isActive = isAlertActive(alert);
            const isDismissed = alert.id && state.dismissedAlerts.has(alert.id);
            return matchesType && isActive && !isDismissed;
        }), [
        siteAlerts,
        alertTypeGuid,
        state.dismissedAlerts
    ]);
    const dismissAlert = (alertId)=>{
        setState((prev)=>({
                ...prev,
                dismissing: alertId
            }));
    };
    const handleDismissed = (alertId)=>{
        Cookies.set(COOKIE_PREFIX + alertId, '1');
        startTransition(()=>{
            setState((prev)=>{
                const next = new Set(prev.dismissedAlerts);
                next.add(alertId);
                return {
                    ...prev,
                    dismissedAlerts: next,
                    dismissing: null
                };
            });
        });
    };
    return {
        matchedAlerts,
        state,
        dismissAlert,
        handleDismissed
    };
};
const Alert = (props)=>{
    const alertTypeGuid = props?.params?.['Alert Type']?.replace(/[{}]/g, '');
    const { matchedAlerts, state, dismissAlert, handleDismissed } = useAlerts(alertTypeGuid);
    if (!matchedAlerts.length || state.alreadyRendered) return null;
    const { container, alertStyle, content, text, cta, dismissButton, ctaWrapper, icon } = TAILWIND_VARIANTS();
    return /*#__PURE__*/ _jsx("div", {
        id: "alert-container",
        className: `${container()} ${state.hydrated ? 'block' : 'hidden'}`,
        children: matchedAlerts.map((alert)=>{
            const alertTypeValue = alert.alertType?.jsonValue?.fields?.title?.value?.toLowerCase() || DEFAULT_ALERT_TYPE;
            const isFading = state.dismissing === alert.id;
            return /*#__PURE__*/ _jsxs("div", {
                className: `${alertStyle({
                    type: alertTypeValue
                })} ${isFading ? 'opacity-0' : 'opacity-100'}`,
                role: alertTypeValue === 'priority' ? 'alert' : 'status',
                ...getTestProps(`component-alert-${alert.id}`),
                onTransitionEnd: ()=>{
                    if (isFading && alert.id) {
                        handleDismissed(alert.id);
                    }
                },
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: content(),
                        children: [
                            /*#__PURE__*/ _jsx(SvgIcon, {
                                size: "s",
                                icon: `alert-${alertTypeValue}`,
                                className: icon({
                                    type: alertTypeValue
                                })
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: text(),
                                children: /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                    field: {
                                        value: alert.alertText?.value
                                    },
                                    ...getTestProps(`text`)
                                })
                            }),
                            alert.alertCTA?.jsonValue?.value?.href && /*#__PURE__*/ _jsx("div", {
                                className: ctaWrapper(),
                                children: /*#__PURE__*/ _jsx(LinkWrapper, {
                                    ctaComponentClass: "default",
                                    className: cta(),
                                    ctaVariant: "tertiary",
                                    ctaIcon: "arrow-dash-right",
                                    field: alert.alertCTA.jsonValue,
                                    suppressNewTabIcon: true,
                                    ...getTestProps(`alert-cta`)
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        onClick: ()=>alert.id && dismissAlert(alert.id),
                        className: dismissButton(),
                        "aria-label": "Dismiss alert",
                        ...getTestProps(`alert-dismiss`),
                        children: /*#__PURE__*/ _jsx(RemixIcon, {
                            name: "close-fill",
                            size: "md"
                        })
                    })
                ]
            }, alert.id);
        })
    });
};
export const Default = Alert;
const TAILWIND_VARIANTS = tv({
    slots: {
        container: [
            'alerts-container',
            'w-full',
            'transition-all',
            'duration-500'
        ],
        alertStyle: [
            'flex',
            'items-center',
            'justify-between',
            'pr-4',
            'border-b',
            'border-component-alert-border',
            'transition-all',
            'duration-200',
            'text-color-core-color-1-Navy',
            'transition-opacity',
            'duration-300',
            'ease-in'
        ],
        content: [
            'flex-col',
            'flex',
            'items-start',
            'md:items-center',
            'gap-4',
            'py-spacing-spacing-16',
            'px-layout-base-margin-x',
            'md:flex-row',
            'md:flex-grow',
            'md:justify-center'
        ],
        text: [
            'md:self-center',
            'text-color-core-color-1-Navy'
        ],
        ctaWrapper: [
            'group',
            'flex',
            'items-center',
            'gap-2',
            'md:self-end'
        ],
        cta: [
            'text-typography-font-size-body-medium',
            'relative',
            'font-semibold',
            'group',
            'after:absolute',
            'after:inset-x-0',
            'after:bottom-0',
            'after:h-0.5',
            'after:bg-white',
            'after:transform',
            'after:origin-left',
            'after:scale-x-0',
            'after:transition-transform',
            'after:duration-300',
            'after:ease-out',
            'group-hover:after:scale-x-100'
        ],
        dismissButton: [
            'text-sm',
            'opacity-75',
            'hover:opacity-100',
            'transition-opacity',
            'duration-200',
            'self-start',
            'pt-6',
            'md:self-center',
            'md:pt-0'
        ],
        icon: []
    },
    variants: {
        type: {
            priority: {
                alertStyle: [
                    'bg-component-alert-bg-priority'
                ],
                icon: [
                    'text-color-red-700'
                ]
            },
            neutral: {
                alertStyle: [
                    'bg-component-alert-bg-neutral'
                ],
                icon: [
                    'text-color-core-color-4-skyblue-darkTint'
                ]
            },
            success: {
                alertStyle: [
                    'bg-component-alert-by-success'
                ],
                icon: [
                    'text-color-green-700'
                ]
            }
        }
    }
});
